/**
 * URL / Query String Parser Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function parseSearchParams(params: URLSearchParams): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const [key, value] of params.entries()) {
    const existing = out[key];
    if (existing === undefined) {
      out[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      out[key] = [existing, value];
    }
  }
  return out;
}

export const UrlParserTool: ITransformerTool = {
  id: 'url-parser',
  name: 'URL / Query String Parser',
  description: 'Parse a URL or query string into components',
  version: '1.0.0',
  type: 'transformer',
  category: 'Utilities',
  keywords: ['url', 'query', 'parse', 'params'],

  supportedInputTypes: [SEMANTIC_TYPES.URL, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.JSON,

  attributes: [
    {
      name: 'pretty',
      type: 'boolean',
      label: 'Pretty Print',
      defaultValue: true,
      description: 'Pretty-print the JSON output',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '').trim();
    if (!text) return config.pretty === false ? '{}' : '{\n}\n';

    const pretty = config.pretty !== false;

    try {
      // URL with scheme
      if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(text)) {
        const url = new URL(text);
        const out = {
          href: url.href,
          protocol: url.protocol,
          username: url.username,
          password: url.password,
          host: url.host,
          hostname: url.hostname,
          port: url.port,
          pathname: url.pathname,
          search: url.search,
          hash: url.hash,
          query: parseSearchParams(url.searchParams),
        };
        return pretty ? JSON.stringify(out, null, 2) : JSON.stringify(out);
      }

      // Query string only
      const queryOnly = text.startsWith('?') ? text.slice(1) : text;
      if (queryOnly.includes('=') || queryOnly.includes('&')) {
        const params = new URLSearchParams(queryOnly);
        const out = { query: parseSearchParams(params) };
        return pretty ? JSON.stringify(out, null, 2) : JSON.stringify(out);
      }

      // Fallback: try as a relative URL
      const url = new URL(text, 'https://example.com');
      const out = {
        href: url.href,
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
        query: parseSearchParams(url.searchParams),
      };
      return pretty ? JSON.stringify(out, null, 2) : JSON.stringify(out);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Invalid URL or query string';
      throw ToolError.validation(message);
    }
  },
};
